"use client";

import { useState } from "react";
import { Check, Gift, ShieldCheck } from "lucide-react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { PLANS } from "@/lib/constants/plans";
import { CURRENCY_PRICING, formatMoney, type Currency } from "@/lib/constants/pricing-display";

type Tier = "starter" | "standard" | "high-volume";
type Interval = "monthly" | "yearly";

const TIERS: Tier[] = ["starter", "standard", "high-volume"];
const TRIAL_DAYS = 14;

type Field = "code" | "email" | "plan" | "businessName";

export default function TrialForm({
  initialCode,
  lead,
  initialCurrency,
  canceled,
  trialEndLabel,
}: {
  initialCode: string;
  lead: string;
  initialCurrency: Currency;
  canceled: boolean;
  /** Server-computed "October 12" (keeps render pure). */
  trialEndLabel: string;
}) {
  const [tier, setTier] = useState<Tier>("standard");
  const [interval, setBillingInterval] = useState<Interval>("monthly");
  const [currency, setCurrency] = useState<Currency>(initialCurrency);
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(initialCode);
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string; field?: Field } | null>(null);

  const price = CURRENCY_PRICING[currency][tier];
  const priceLabel =
    interval === "yearly"
      ? `${formatMoney(price.yearlyTotal, currency)} ${currency} per year`
      : `${formatMoney(price.price, currency)} ${currency} per month`;


  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/trial/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessName, email, code, tierKey: tier, billingInterval: interval, currency, lead, website }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setError({ message: data.error || "Something went wrong. Please try again.", field: data.field });
    } catch {
      setError({ message: "Network error. Please check your connection and try again." });
    }
    setLoading(false);
  }

  return (
    <div style={{ fontFamily: "var(--font-body)" }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 14px",
          borderRadius: 999,
          background: "rgba(99,102,241,0.08)",
          border: "1px solid rgba(99,102,241,0.18)",
          color: "#4f46e5",
          fontSize: "0.8rem",
          fontWeight: 600,
          marginBottom: 16,
        }}
      >
        <Gift size={15} /> {TRIAL_DAYS}-day free trial
      </div>
      <h1 style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(1.5rem, 4vw, 1.9rem)", fontWeight: 700, margin: "0 0 6px", color: "#0f172a" }}>
        Try your AI receptionist free for {TRIAL_DAYS} days
      </h1>
      <p style={{ color: "var(--color-text-muted)", fontSize: "0.92rem", lineHeight: 1.6, margin: "0 0 24px" }}>
        Pick a plan and enter the trial code from your Ethos Virtual rep. You won&apos;t be charged today. Your plan
        starts on {trialEndLabel} unless you cancel before then.
      </p>

      {canceled && !error && (
        <div style={notice("info")}>Checkout was canceled. Your trial hasn&apos;t started; you can pick up where you left off.</div>
      )}
      {error && <div style={notice("error")} role="alert">{error.message}</div>}

      <form onSubmit={handleSubmit} noValidate>
        {/* Currency + interval */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <Segmented
            label="Billing"
            value={interval}
            options={[
              { value: "monthly", label: "Monthly" },
              { value: "yearly", label: "Yearly (save 10%)" },
            ]}
            onChange={(v) => setBillingInterval(v as Interval)}
          />
          <Segmented
            label="Currency"
            value={currency}
            options={[
              { value: "USD", label: "USD" },
              { value: "CAD", label: "CAD" },
            ]}
            onChange={(v) => setCurrency(v as Currency)}
          />
        </div>

        {/* Plans */}
        <div role="radiogroup" aria-label="Plan" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 20 }}>
          {TIERS.map((t) => {
            const p = PLANS[t];
            const cp = CURRENCY_PRICING[currency][t];
            const selected = t === tier;
            return (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setTier(t)}
                style={{
                  textAlign: "left",
                  padding: "14px 14px 12px",
                  borderRadius: 14,
                  cursor: "pointer",
                  background: selected ? "rgba(99,102,241,0.07)" : "rgba(255,255,255,0.7)",
                  border: selected ? "2px solid #6366f1" : "1px solid rgba(15,23,42,0.1)",
                  margin: selected ? 0 : 1,
                  fontFamily: "var(--font-body)",
                  color: "#0f172a",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 650, fontSize: "0.98rem" }}>{p.name}</span>
                  {selected && (
                    <span style={{ width: 20, height: 20, borderRadius: 999, background: "#6366f1", color: "#fff", display: "grid", placeItems: "center" }}>
                      <Check size={13} />
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "1.25rem", fontWeight: 700, margin: "8px 0 2px" }}>
                  {formatMoney(interval === "yearly" ? cp.yearlyTotal : cp.price, currency)}
                  <span style={{ fontSize: "0.78rem", fontWeight: 500, color: "var(--color-text-muted)" }}>
                    {" "}
                    {currency}/{interval === "yearly" ? "yr" : "mo"}
                  </span>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{p.includedMinutes} minutes a month</div>
                <div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)", marginTop: 2 }}>{p.tagline}</div>
              </button>
            );
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          <Input
            label="Business name"
            placeholder="Maple Street Plumbing"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            error={error?.field === "businessName" ? " " : undefined}
            autoComplete="organization"
            required
          />
          <Input
            label="Work email"
            type="email"
            placeholder="you@business.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error?.field === "email" ? " " : undefined}
            autoComplete="email"
            required
          />
        </div>
        <div style={{ marginTop: 14 }}>
          <Input
            label="Trial code"
            placeholder="Enter the code from your rep"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            error={error?.field === "code" ? " " : undefined}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </div>
        {/* Honeypot, hidden from people */}
        <div aria-hidden="true" style={{ position: "absolute", left: -10000, width: 1, height: 1, overflow: "hidden" }}>
          <label>
            Website
            <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </label>
        </div>

        <div
          style={{
            marginTop: 20,
            padding: "14px 16px",
            borderRadius: 12,
            background: "rgba(15,23,42,0.03)",
            border: "1px solid rgba(15,23,42,0.06)",
            fontSize: "0.86rem",
            lineHeight: 1.6,
            color: "#334155",
          }}
        >
          <strong>Today: $0.</strong> {PLANS[tier].name} is free for {TRIAL_DAYS} days with up to {PLANS[tier].includedMinutes} call
          minutes. On {trialEndLabel} your plan starts at {priceLabel} using the card you add at checkout. Cancel anytime before then
          and you won&apos;t be charged.
        </div>

        <div style={{ marginTop: 18 }}>
          <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
            Start my free trial
          </Button>
        </div>
        <p style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: "0.76rem", color: "var(--color-text-muted)", margin: "12px 0 0" }}>
          <ShieldCheck size={14} /> Secure checkout. Your card is only charged when your plan starts.
        </p>
      </form>
    </div>
  );
}

function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: "inline-flex", padding: 3, borderRadius: 999, background: "rgba(15,23,42,0.05)", border: "1px solid rgba(15,23,42,0.06)" }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            style={{
              border: "none",
              cursor: "pointer",
              padding: "7px 14px",
              borderRadius: 999,
              fontSize: "0.82rem",
              fontWeight: 600,
              fontFamily: "var(--font-body)",
              background: active ? "#ffffff" : "transparent",
              color: active ? "#0f172a" : "#64748b",
              boxShadow: active ? "0 1px 3px rgba(15,23,42,0.12)" : "none",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function notice(kind: "info" | "error"): React.CSSProperties {
  return {
    background: kind === "error" ? "rgba(239,68,68,0.08)" : "rgba(99,102,241,0.06)",
    border: `1px solid ${kind === "error" ? "rgba(239,68,68,0.2)" : "rgba(99,102,241,0.18)"}`,
    color: kind === "error" ? "#b91c1c" : "#4338ca",
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: "0.84rem",
    lineHeight: 1.5,
    marginBottom: 16,
  };
}
